import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { AuthGuard, TranslateResolver } from '@motivforce/mx-library-angular';

import { DistributorReferralPageComponent } from './core/components/distributor-referral-page/distributor-referral-page.component';
import { DistributorGuard } from './core/guards/distributor-guard';
import { UserGuard } from './core/guards/user-guard';

const routes: Routes = [
  {
    path: '',
    redirectTo: 'landing',
    pathMatch: 'full',
  },
  {
    path: 'landing',
    data: {
      isLanding: true,
      pageTitle: (data: any) => `${data.translate.instant('Landing')}`,
    },
    resolve: { translate: TranslateResolver },
    loadChildren: () => import('./landing/landing.module').then((m) => m.LandingModule),
    canActivate: [],
  },
  {
    path: 'auth',
    loadChildren: () => import('./authentication/authentication.module').then((m) => m.AuthenticationModule),
    canActivate: [],
  },
  {
    path: 'registration',
    loadChildren: () => import('./registration/registration.module').then((m) => m.RegistrationModule),
    canActivate: [],
  },
  {
    path: 'thank-you',
    data: {
      pageTitle: (data: any) => `${data.translate.instant('Landing')}`,
    },
    resolve: { translate: TranslateResolver },
    loadChildren: () => import('@motivforce/mx-library-angular').then((m) => m.ContentModule),
    canActivate: [],
  },
  {
    path: 'home',
    data: {
      pageTitle: (data: any) => `${data.translate.instant('Home')}`,
    },
    resolve: { translate: TranslateResolver },
    loadChildren: () => import('./home/home.module').then((m) => m.HomeModule),
    canActivate: [AuthGuard, DistributorGuard, UserGuard],
  },
  {
    path: 'sales-accelerators',
    data: {
      breadcrumbLabel: (data: any) => data.translate.instant('Sales accelerators'),
      pageTitle: (data: any) => `${data.translate.instant('Sales accelerators')}`,
    },
    resolve: { translate: TranslateResolver },
    loadChildren: () => import('./sales-accelerators/sales-accelerators.module').then((m) => m.SalesAcceleratorsModule),
    canActivate: [AuthGuard, UserGuard],
  },
  {
    path: 'elevate-partners',
    data: {
      breadcrumbLabel: (data: any) => data.translate.instant('Elevate partners'),
      pageTitle: (data: any) => `${data.translate.instant('Elevate partners')}`,
    },
    resolve: { translate: TranslateResolver },
    loadChildren: () => import('./elevate-partners/elevate-partners.module').then((m) => m.ElevatePartnersModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'terms-and-conditions',
    data: {
      breadcrumbLabel: (data: any) => data.translate.instant('Terms and conditions'),
      pageTitle: (data: any) => `${data.translate.instant('Terms and conditions')}`,
    },
    resolve: { translate: TranslateResolver },
    loadChildren: () => import('@motivforce/mx-library-angular').then((m) => m.ContentModule),
    canActivate: [],
  },
  {
    path: 'support',
    loadChildren: () => import('./support/support.module').then((m) => m.SupportModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'account-summary',
    data: {
      breadcrumbLabel: (data: any) => data.translate.instant('Account summary'),
      pageTitle: (data: any) => `${data.translate.instant('Account summary')}`,
    },
    resolve: { translate: TranslateResolver },
    loadChildren: () => import('./account-summary/account-summary.module').then((m) => m.AccountSummaryModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'account-summary-statement',
    loadChildren: () => import('./account-summary/account-summary.module').then((m) => m.AccountSummaryModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'promotions',
    loadChildren: () => import('./promotion/promotion.module').then((m) => m.PromotionModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'program-overview',
    data: {
      breadcrumbLabel: (data: any) => data.translate.instant('Program overview'),
      pageTitle: (data: any) => `${data.translate.instant('Program overview')}`,
    },
    resolve: { translate: TranslateResolver },
    loadChildren: () => import('./program-overview/program-overview.module').then((m) => m.ProgramOverviewModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'enrolment',
    data: {
      breadcrumbLabel: (data: any) => data.translate.instant('Enrolment'),
      pageTitle: (data: any) => `${data.translate.instant('Enrolment')}`,
    },
    resolve: { translate: TranslateResolver },
    loadChildren: () => import('./enrolment/enrolment.module').then((m) => m.EnrolmentModule),
    canActivate: [AuthGuard, UserGuard],
  },
  {
    path: 'company-account',
    data: {
      breadcrumbLabel: (data: any) => data.translate.instant('Company account'),
      pageTitle: (data: any) => `${data.translate.instant('Company account')}`,
    },
    resolve: { translate: TranslateResolver },
    loadChildren: () => import('./company-account/company-account.module').then((m) => m.CompanyAccountModule),
    canActivate: [AuthGuard, UserGuard],
  },
  {
    path: 'distributors',
    data: {
      breadcrumbLabel: (data: any) => data.translate.instant('Distributors'),
      pageTitle: (data: any) => `${data.translate.instant('Distributors')}`,
    },
    resolve: { translate: TranslateResolver },
    loadChildren: () => import('./distributors/distributors.module').then((m) => m.DistributorsModule),
    canActivate: [AuthGuard, DistributorGuard],
  },
  {
    path: 'distributor-referral',
    component: DistributorReferralPageComponent,
  },
  {
    path: 'rebate-claim',
    data: {
      breadcrumbLabel: (data: any) => data.translate.instant('Claim your rebate'),
      pageTitle: (data: any) => `${data.translate.instant('Claim your rebate')}`,
    },
    resolve: { translate: TranslateResolver },
    loadChildren: () => import('./invoice-upload/invoice-upload.module').then((m) => m.InvoiceUploadModule),
    canActivate: [AuthGuard, UserGuard],
  },
  {
    path: 'not-allowed',
    data: {
      breadcrumbLabel: (data: any) => data.translate.instant('Not allowed'),
      pageTitle: (data: any) => `${data.translate.instant('Not allowed')}`,
    },
    resolve: { translate: TranslateResolver },
    loadChildren: () => import('./not-allowed/not-allowed.module').then((m) => m.NotAllowedModule),
    canActivate: [],
  },
  {
    path: 'spin-to-win',
    data: {
      breadcrumbLabel: (data: any) => data.translate.instant('Spin to win'),
      pageTitle: (data: any) => `${data.translate.instant('Spin to win')}`,
    },
    resolve: { translate: TranslateResolver },
    loadChildren: () => import('./spin-to-win/spin-to-win.module').then((m) => m.SpinToWinModule),
    canActivate: [],
  },
  {
    path: 'leaderboards',
    data: {
      breadcrumbLabel: (data: any) => data.translate.instant('Leaderboards'),
      pageTitle: (data: any) => `${data.translate.instant('Leaderboards')}`,
    },
    loadChildren: () => import('./leaderboards/leaderboards.module').then((m) => m.LeaderboardsModule),
    resolve: { translate: TranslateResolver },

    canActivate: [AuthGuard],
  },
  {
    path: '**',
    redirectTo: '/landing',
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'top',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
